'use client';
export interface Activity {
  activity_id: number;
  name: string;
  start: Date | string;
  proc: keyof typeof ActivityProcStatus;
  pct: number;
  /**
   * Meters/second
   */
  average_speed: number;
  /**
   * seconds
   */
  elapsed_time: number;
  /**
   * seconds
   */
  moving_time: number;
  /**
   * meters
   */
  distance: number;
  calories: number;
  max_speed: number;
  average_cadence: number;
  average_temp: number;
  average_watts: number;
  weighted_average_watts: number;
  total_elevation_gain: number;
  kilojoules: number;
  max_watts: number;
  elev_high: number;
  elev_low: number;
  description: string;
  version: number;
  type: (typeof ActivityTypes)[number];
}

export const ActivityTypes = ['Run', 'Ride', 'Walk', 'Hike'] as const;

export const ActivityProcStatus = {
  // CAT_RUN: 0,
  // CAT_CYCLE: 20,

  // MASK_RUN: 0b0001,
  // MASK_CYC: 0b0010,

  UNDOWNLOADED: 0,
  UNPROCESSED: 1,
  QUEUED: 2,
  PROCESSED: 3,
  NO_CITY: 4,
  NO_TRACK_DATA: 5,
  INVALID_TYPE: 6,
  DELETED: 7,
  ACCESS_DENIED: 8,
  BBOX_TOO_BIG: 9,

  CYCLE_PROCESSED: 23,
} as const;

export const ActivityStatusCodes = Object.values(ActivityProcStatus);
export type ActivityStatusCode = (typeof ActivityStatusCodes)[number];

export const ActivityStatusWithMessage = {
  [ActivityProcStatus.UNDOWNLOADED]: {
    type: getStatusType(ActivityProcStatus.UNDOWNLOADED),
    message: 'Waiting to download',
  },
  [ActivityProcStatus.UNPROCESSED]: {
    type: getStatusType(ActivityProcStatus.UNPROCESSED),
    message: 'Downloaded',
  },
  [ActivityProcStatus.QUEUED]: {
    type: getStatusType(ActivityProcStatus.QUEUED),
    message: 'Queued',
  },
  [ActivityProcStatus.PROCESSED]: {
    type: getStatusType(ActivityProcStatus.PROCESSED),
    message: 'Processed',
  },
  [ActivityProcStatus.CYCLE_PROCESSED]: {
    type: getStatusType(ActivityProcStatus.CYCLE_PROCESSED),
    message: 'Processed',
  },
  [ActivityProcStatus.NO_CITY]: {
    type: getStatusType(ActivityProcStatus.NO_CITY),
    message: 'No matching cities',
  },
  [ActivityProcStatus.NO_TRACK_DATA]: {
    type: getStatusType(ActivityProcStatus.NO_TRACK_DATA),
    message: 'No position data',
  },
  [ActivityProcStatus.INVALID_TYPE]: {
    type: getStatusType(ActivityProcStatus.INVALID_TYPE),
    message: 'Not supported',
  },
  [ActivityProcStatus.DELETED]: {
    type: getStatusType(ActivityProcStatus.DELETED),
    message: 'Activity deleted',
  },
  [ActivityProcStatus.ACCESS_DENIED]: {
    type: getStatusType(ActivityProcStatus.ACCESS_DENIED),
    message: 'Access denied',
  },
  [ActivityProcStatus.BBOX_TOO_BIG]: {
    type: getStatusType(ActivityProcStatus.BBOX_TOO_BIG),
    message: 'Activity too large',
  },
} as const;

export function isProcessed(proc: ActivityStatusCode) {
  const ProcessedStatusCodes = [
    ActivityProcStatus.PROCESSED,
    ActivityProcStatus.CYCLE_PROCESSED,
  ] as const;
  return !!ProcessedStatusCodes?.find(status => status === proc);
}

export function isProcessing(proc: ActivityStatusCode) {
  const IsProcessingStatusCodes = [
    ActivityProcStatus.UNPROCESSED,
    ActivityProcStatus.UNDOWNLOADED,
  ] as const;
  return !![...IsProcessingStatusCodes].find(status => status === proc);
}

export function isQueued(proc: ActivityStatusCode) {
  const QueuedStatusCodes = [ActivityProcStatus.QUEUED] as const;
  return !!QueuedStatusCodes.find(status => status === proc);
}

export function isFailed(proc: ActivityStatusCode) {
  const FailedStatusCodes = [
    ActivityProcStatus.NO_CITY,
    ActivityProcStatus.NO_TRACK_DATA,
    ActivityProcStatus.INVALID_TYPE,
    ActivityProcStatus.DELETED,
    ActivityProcStatus.ACCESS_DENIED,
    ActivityProcStatus.BBOX_TOO_BIG,
  ] as const;
  return !!FailedStatusCodes.find(status => status === proc);
}

export function getStatusType(proc: ActivityStatusCode) {
  if (isProcessed(proc)) {
    return 'processed';
  }
  if (isProcessing(proc)) {
    return 'processing';
  }
  if (isQueued(proc)) {
    return 'queued';
  }
  if (isFailed(proc)) {
    return 'error';
  }
  return 'default';
}

export type ActivityTypeCode = ReturnType<typeof getStatusType>;
